import type { InventoryVehicleIdIdentifier } from "@slfinrtl/ui-bff-routes"
import { useMemo, useState } from "react"
import { useIntl } from "react-intl"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { offerMessages } from "src/screens/offers/common/offer.messages"
import { useCap } from "src/screens/offers/common/useCap"
import { useOfferVehicleData } from "src/screens/offers/common/useOfferVehicleData"
import type { UseTransferVehicleModalProps } from "../useTransferVehicleModal"
import { useTransferVehicleModal } from "../useTransferVehicleModal"
import { TransferVehicleModalContent } from "./TransferVehicleModalContent"

export type TransferVehicleModalProps = Omit<UseTransferVehicleModalProps, "modalId">

type Props = ModalProps<TransferVehicleModalProps>

export const TransferVehicleModal = withErrorHandler(({ id, modalProps }: Props) => {
	const { formatMessage } = useIntl()
	const [hasCapError, setHasCapError] = useState(false)

	const { customer, isTransferring, onCancel, onConfirmTransfer, onCustomerSelect } = useTransferVehicleModal({
		modalId: id,
		...modalProps,
	})

	const vehicleIdentifier: InventoryVehicleIdIdentifier = useMemo(
		() => ({
			type: "INVENTORY_VEHICLE_ID",
			vehicleId: modalProps.vehicleId,
		}),
		[modalProps.vehicleId],
	)

	const { hasError: hasVehicleDataError, view: vehicleDataView } = useOfferVehicleData({
		vehicleIdentifier,
	})

	const { isCapInitialized, isCustomerNameLoading, selectCustomerFromCap } = useCap({
		onCustomerSelect: (selectedCustomer) => {
			setHasCapError(false)
			onCustomerSelect(selectedCustomer)
		},
		onError: () => {
			setHasCapError(true)
		},
		orderTypeCode: vehicleDataView?.orderTypeCode,
	})

	return (
		<Modal id={id} heading={formatMessage(offerMessages.transferVehicleModalTitle)} disableBackdropClick>
			<TransferVehicleModalContent
				customer={customer}
				hasCapError={hasCapError}
				hasVehicleDataError={hasVehicleDataError}
				isCapInitialized={isCapInitialized}
				isCustomerNameLoading={isCustomerNameLoading}
				isTransferring={isTransferring}
				hasVehicleData={!!vehicleDataView}
				onCancel={onCancel}
				onCapErrorDismiss={() => setHasCapError(false)}
				onConfirmTransfer={onConfirmTransfer}
				selectCustomerFromCap={selectCustomerFromCap}
			/>
		</Modal>
	)
})
